import { RisOrg } from "../../types/models/RisOrg";
import { RisAccessLevel, RisOrgUser, RisUpdateUserRequest } from "../../types/models/RisOrgUser";

/**
 * Mirrors ris-api's access rules so the UI only offers actions that will be allowed. ris-api
 * makes the real decision; these only decide what to show.
 *
 * - Admins manage everyone else. Only users in the KiwiRail org can be Admins.
 * - Org managers manage non-Admins in their own org (the API only returns their org's users).
 * - Everyone can change their own name, but nobody can change their own expiry, access level,
 *   org or active state.
 */
export interface Caller {
  /** Lowercased access level from the token: "admin", "orgmanager" or "user" */
  accessLevel: string;
  email: string;
}

export type AssignableAccessLevel = NonNullable<RisUpdateUserRequest["AccessLevel"]>;

export const accessLevelLabels: Record<AssignableAccessLevel, string> = {
  Admin: "Admin",
  OrgManager: "Org Manager",
  User: "User",
};

/** RIS returns "Orgmanager"; requests use "OrgManager". */
export const toAssignableAccessLevel = (level: RisAccessLevel): AssignableAccessLevel =>
  level === "Orgmanager" ? "OrgManager" : level;

export const isSelf = (caller: Caller, user: RisOrgUser) =>
  caller.email.toLowerCase() === user.Email.toLowerCase();

/** Whether the caller can act on someone else (expiry, access level, deactivate, remove). */
export const canManageUser = (caller: Caller, user: RisOrgUser) => {
  if (isSelf(caller, user)) return false;
  if (caller.accessLevel === "admin") return true;
  return caller.accessLevel === "orgmanager" && user.AccessLevel !== "Admin";
};

export const canEditName = (caller: Caller, user: RisOrgUser) =>
  isSelf(caller, user) || canManageUser(caller, user);

/** The KiwiRail org is identified by its email suffix, as in ris-api. */
export const isKiwiRailOrg = (org?: RisOrg) => org?.EmailSuffix?.toLowerCase() === "kiwirail.co.nz";

export const assignableAccessLevels = (
  caller: Caller,
  userInKiwiRailOrg: boolean
): AssignableAccessLevel[] =>
  caller.accessLevel === "admin" && userInKiwiRailOrg
    ? ["Admin", "OrgManager", "User"]
    : ["OrgManager", "User"];
